import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

export type WorkbenchCollectionKey = string | number;
export type WorkbenchCollectionSelectionMode = 'none' | 'single' | 'multiple';
export type WorkbenchCollectionSelectionBehavior = 'replace' | 'toggle' | 'range';

export interface WorkbenchCollectionSelectionIntent {
  behavior?: WorkbenchCollectionSelectionBehavior;
  shiftKey?: boolean;
}

export interface WorkbenchCollectionSelectionChangeDetails<Item> {
  item: Item;
  index: number;
  key: WorkbenchCollectionKey;
  behavior: WorkbenchCollectionSelectionBehavior;
}

export interface UseWorkbenchCollectionSelectionOptions<Item> {
  items: readonly Item[];
  getItemKey: (item: Item, index: number) => WorkbenchCollectionKey;
  mode?: WorkbenchCollectionSelectionMode;
  behavior?: WorkbenchCollectionSelectionBehavior;
  selectedKeys?: Iterable<WorkbenchCollectionKey>;
  defaultSelectedKeys?: Iterable<WorkbenchCollectionKey>;
  isItemDisabled?: (item: Item, index: number) => boolean;
  onSelectionChange?: (
    selectedKeys: ReadonlySet<WorkbenchCollectionKey>,
    details: WorkbenchCollectionSelectionChangeDetails<Item>,
  ) => void;
}

export interface WorkbenchCollectionSelectionController {
  mode: WorkbenchCollectionSelectionMode;
  selectedKeys: ReadonlySet<WorkbenchCollectionKey>;
  isSelected: (key: WorkbenchCollectionKey) => boolean;
  selectKey: (key: WorkbenchCollectionKey, intent?: WorkbenchCollectionSelectionIntent) => void;
  resetAnchor: () => void;
}

interface NormalizedSelectionItem<Item> {
  item: Item;
  index: number;
  key: WorkbenchCollectionKey;
}

function setsEqual<Value>(left: ReadonlySet<Value>, right: ReadonlySet<Value>) {
  if (left.size !== right.size) return false;
  for (const value of left) {
    if (!right.has(value)) return false;
  }
  return true;
}

function getComparableKey(key: WorkbenchCollectionKey) {
  return String(key);
}

function getDefaultBehavior(mode: WorkbenchCollectionSelectionMode) {
  return mode === 'multiple' ? 'toggle' : 'replace';
}

export function useWorkbenchCollectionSelection<Item>({
  items,
  getItemKey,
  mode = 'none',
  behavior,
  selectedKeys: controlledSelectedKeys,
  defaultSelectedKeys,
  isItemDisabled,
  onSelectionChange,
}: UseWorkbenchCollectionSelectionOptions<Item>): WorkbenchCollectionSelectionController {
  const controlled = controlledSelectedKeys !== undefined;
  const [internalSelectedKeys, setInternalSelectedKeys] = useState<Set<WorkbenchCollectionKey>>(
    () => new Set(defaultSelectedKeys ?? []),
  );
  const anchorKeyRef = useRef<WorkbenchCollectionKey | null>(null);
  const onSelectionChangeRef = useRef(onSelectionChange);
  onSelectionChangeRef.current = onSelectionChange;

  const normalizedItems = useMemo<NormalizedSelectionItem<Item>[]>(() => {
    const seenKeys = new Map<string, number>();
    return items.map((item, index) => {
      const key = getItemKey(item, index);
      const comparableKey = getComparableKey(key);
      const duplicateIndex = seenKeys.get(comparableKey);
      if (duplicateIndex !== undefined) {
        throw new TypeError(
          `useWorkbenchCollectionSelection received duplicate item key "${comparableKey}" at indices ${duplicateIndex} and ${index}.`,
        );
      }
      seenKeys.set(comparableKey, index);
      return { item, index, key };
    });
  }, [getItemKey, items]);

  const itemByKey = useMemo(() => {
    const entries = new Map<WorkbenchCollectionKey, NormalizedSelectionItem<Item>>();
    normalizedItems.forEach((entry) => entries.set(entry.key, entry));
    return entries;
  }, [normalizedItems]);

  const selectedKeys = useMemo<ReadonlySet<WorkbenchCollectionKey>>(
    () => mode === 'none'
      ? new Set()
      : controlled
        ? new Set(controlledSelectedKeys ?? [])
        : internalSelectedKeys,
    [controlled, controlledSelectedKeys, internalSelectedKeys, mode],
  );
  const selectedKeysRef = useRef(selectedKeys);
  selectedKeysRef.current = selectedKeys;

  const resetAnchor = useCallback(() => {
    anchorKeyRef.current = null;
  }, []);

  useEffect(() => {
    if (controlled) return;
    const availableKeys = new Set(normalizedItems.map(({ key }) => key));
    setInternalSelectedKeys((previous) => {
      const next = new Set(Array.from(previous).filter((key) => availableKeys.has(key)));
      if (setsEqual(previous, next)) return previous;
      selectedKeysRef.current = next;
      return next;
    });
  }, [controlled, normalizedItems]);

  useEffect(() => {
    const anchorKey = anchorKeyRef.current;
    if (mode !== 'multiple' || selectedKeys.size === 0 || anchorKey === null) {
      resetAnchor();
      return;
    }
    const anchor = itemByKey.get(anchorKey);
    if (!anchor || isItemDisabled?.(anchor.item, anchor.index)) resetAnchor();
  }, [isItemDisabled, itemByKey, mode, resetAnchor, selectedKeys.size]);

  const selectKey = useCallback((
    key: WorkbenchCollectionKey,
    intent: WorkbenchCollectionSelectionIntent = {},
  ) => {
    if (mode === 'none') return;
    const entry = itemByKey.get(key);
    if (!entry || isItemDisabled?.(entry.item, entry.index)) return;

    const current = new Set(selectedKeysRef.current);
    const configuredBehavior = behavior ?? getDefaultBehavior(mode);
    let effectiveBehavior = intent.shiftKey && mode === 'multiple'
      ? 'range'
      : intent.behavior ?? configuredBehavior;

    if (mode === 'single') {
      effectiveBehavior = effectiveBehavior === 'toggle' ? 'toggle' : 'replace';
    }

    const anchor = anchorKeyRef.current === null
      ? undefined
      : itemByKey.get(anchorKeyRef.current);
    const hasValidAnchor = Boolean(
      anchor && !isItemDisabled?.(anchor.item, anchor.index),
    );
    if (mode === 'multiple' && effectiveBehavior === 'range' && !hasValidAnchor) {
      effectiveBehavior = configuredBehavior === 'range' ? 'toggle' : configuredBehavior;
    }

    const next = new Set<WorkbenchCollectionKey>();
    if (mode === 'single') {
      if (!(effectiveBehavior === 'toggle' && current.has(entry.key))) next.add(entry.key);
    } else if (effectiveBehavior === 'range' && anchor) {
      current.forEach((selectedKey) => next.add(selectedKey));
      const start = Math.min(anchor.index, entry.index);
      const end = Math.max(anchor.index, entry.index);
      normalizedItems.slice(start, end + 1).forEach((candidate) => {
        if (!isItemDisabled?.(candidate.item, candidate.index)) next.add(candidate.key);
      });
    } else if (effectiveBehavior === 'replace') {
      next.add(entry.key);
    } else {
      current.forEach((selectedKey) => next.add(selectedKey));
      if (next.has(entry.key)) next.delete(entry.key);
      else next.add(entry.key);
    }

    anchorKeyRef.current = mode === 'multiple' ? entry.key : null;
    if (!controlled) {
      selectedKeysRef.current = next;
      setInternalSelectedKeys(next);
    }
    onSelectionChangeRef.current?.(next, {
      item: entry.item,
      index: entry.index,
      key: entry.key,
      behavior: effectiveBehavior,
    });
  }, [behavior, controlled, isItemDisabled, itemByKey, mode, normalizedItems]);

  const isSelected = useCallback(
    (key: WorkbenchCollectionKey) => selectedKeys.has(key),
    [selectedKeys],
  );

  return {
    mode,
    selectedKeys,
    isSelected,
    selectKey,
    resetAnchor,
  };
}
